import { useEffect, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { Illustration } from 'foundations-components/transitional/components/Illustration';
import { SelectableButton } from 'foundations-components/transitional/components/SelectableButton';
import { SuccessIcon } from 'foundations-assets/icons/components/SuccessIcon';
import { getUserInfo } from '../common/api/getUserInfo';
import { track } from '../common/eventTracking/tracker';
import { getRepName, getUpgradeDataTrackingProperties } from '../../ums/utils';
import { StyledModal } from '../common/components/StyledModal';
import { trackTalkToSalesPql } from '../utils/metricUtils';
import EmailModal from './EmailModal';
import MeetingModal from './MeetingModal';
import { MODAL_TYPES } from './constants';
import { GetACallSection } from './AssignableModal/GetACallSection';
import { loadAndOpenSalesChat } from '../utils/loadAndOpenSalesChat';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const VerticalAssignableModal = ({
  handleIframeSizeChange = () => {},
  hideBackground = false,
  onClose,
  _overrideModalHeaderText,
  _overrideCommunicationMethodKey,
  upgradeData,
  modalRoot,
  repInfo,
  isFreePortal
}) => {
  const [currentModalView, setCurrentModalView] = useState(_overrideCommunicationMethodKey || MODAL_TYPES.ASSIGNABLE);
  const [showGetACallTitle, setShowGetACallTitle] = useState(true);
  const [firstName, setFirstName] = useState('');
  useEffect(() => {
    track('communicationMethodsInteraction', Object.assign({
      action: 'viewed'
    }, getUpgradeDataTrackingProperties(upgradeData), {
      repName: getRepName(repInfo)
    }));
    trackTalkToSalesPql(upgradeData.app);
    void getUserInfo().then(user => {
      setFirstName(user.user.first_name);
    });
  }, [upgradeData, repInfo]);
  const handleBackClick = () => {
    handleIframeSizeChange(MODAL_TYPES.ASSIGNABLE);
    setCurrentModalView(MODAL_TYPES.ASSIGNABLE);
  };
  const commonProps = {
    hideBackground,
    onClose,
    upgradeData,
    repInfo
  };
  if (currentModalView === MODAL_TYPES.MEETING) {
    return /*#__PURE__*/_jsx(MeetingModal, Object.assign({}, commonProps, {
      onBackClick: handleBackClick
    }));
  }
  if (currentModalView === MODAL_TYPES.EMAIL) {
    return /*#__PURE__*/_jsx(EmailModal, Object.assign({}, commonProps, {
      onBackClick: handleBackClick
    }));
  }
  const handleClick = (communicationKey, trackingAction) => {
    track('communicationMethodsInteraction', Object.assign({
      action: trackingAction
    }, getUpgradeDataTrackingProperties(upgradeData), {
      repName: getRepName(repInfo)
    }));
    handleIframeSizeChange(communicationKey);
    setCurrentModalView(communicationKey);
  };
  const handleClickChat = () => {
    loadAndOpenSalesChat({
      upgradeData,
      isRetail: false,
      isAssignable: true
    });
    track('communicationMethodsInteraction', Object.assign({
      action: 'clicked chat with us'
    }, getUpgradeDataTrackingProperties(upgradeData), {
      repName: getRepName(repInfo)
    }));
    onClose();
  };
  const optionalProps = modalRoot ? {
    rootNode: modalRoot
  } : {};
  return /*#__PURE__*/_jsxs(StyledModal, Object.assign({
    "data-test-id": "communication-methods-modal",
    hideBackground: hideBackground,
    width: 750,
    use: "conversational"
  }, optionalProps, {
    children: [currentModalView === MODAL_TYPES.GET_A_CALL && (showGetACallTitle ? /*#__PURE__*/_jsxs(DialogHeader, {
      marginBlockEnd: "800",
      children: [/*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: () => onClose({
          isClosedOnOptionsView: currentModalView === MODAL_TYPES.ASSIGNABLE
        })
      }), /*#__PURE__*/_jsx(Text, {
        as: "h2",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "upgrades.communicationMethods.callTitle"
        })
      })]
    }) : /*#__PURE__*/_jsx(DialogHeader, {
      padding: "0",
      children: /*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: () => onClose({
          isClosedOnOptionsView: currentModalView === MODAL_TYPES.ASSIGNABLE
        })
      })
    })), currentModalView !== MODAL_TYPES.GET_A_CALL && /*#__PURE__*/_jsxs(DialogHeader, {
      marginBlockEnd: "800",
      children: [/*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: () => onClose({
          isClosedOnOptionsView: currentModalView === MODAL_TYPES.ASSIGNABLE
        })
      }), /*#__PURE__*/_jsx(Text, {
        textAlign: "start",
        marginBlockStart: "-800",
        children: _overrideModalHeaderText || /*#__PURE__*/_jsx(FormattedMessage, {
          message: "upgrades.communicationMethods.expertTitle"
        })
      })]
    }), /*#__PURE__*/_jsx(DialogBody, {
      children: currentModalView === MODAL_TYPES.GET_A_CALL ? /*#__PURE__*/_jsx(GetACallSection, {
        onBackClick: handleBackClick,
        upgradeData: upgradeData,
        handleIframeSizeChange: handleIframeSizeChange,
        repInfo: repInfo,
        setShowGetACallTitle: setShowGetACallTitle
      }) : /*#__PURE__*/_jsxs("div", {
        "data-test-id": "assignable-modal-body",
        children: [/*#__PURE__*/_jsx(Text, {
          as: "h1",
          textAlign: "start",
          marginBlockEnd: "600",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "upgrades.communicationMethods.expertSubtitle",
            options: {
              firstName
            }
          })
        }), /*#__PURE__*/_jsxs(Flex, {
          flexDirection: "column",
          gap: "300",
          children: [/*#__PURE__*/_jsx(SelectableButton, {
            "data-test-id": "communication-method-meeting",
            size: "large",
            block: true,
            blockSize: "165px",
            onClick: () => handleClick(MODAL_TYPES.MEETING, 'clicked contact sales with meeting'),
            title: /*#__PURE__*/_jsxs(Flex, {
              flexDirection: "row",
              gap: "400",
              alignItems: "start",
              paddingInlineStart: "400",
              paddingBlockEnd: "400",
              children: [/*#__PURE__*/_jsx(Illustration, {
                name: "meetings-small",
                height: 75
              }), /*#__PURE__*/_jsxs(Flex, {
                flexDirection: "column",
                gap: "200",
                alignItems: "start",
                children: [/*#__PURE__*/_jsxs(Flex, {
                  flexDirection: "row",
                  gap: "200",
                  alignItems: "center",
                  children: [/*#__PURE__*/_jsx(Text, {
                    as: "h5",
                    children: /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "upgrades.communicationMethods.meeting"
                    })
                  }), /*#__PURE__*/_jsx(Badge, {
                    children: /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "upgrades.communicationMethods.recommended"
                    })
                  })]
                }), /*#__PURE__*/_jsxs(Flex, {
                  flexDirection: "column",
                  gap: "100",
                  alignItems: "start",
                  children: [/*#__PURE__*/_jsxs(Flex, {
                    flexDirection: "row",
                    gap: "200",
                    alignItems: "center",
                    children: [/*#__PURE__*/_jsx(SuccessIcon, {
                      color: "fill-accent-green-default"
                    }), /*#__PURE__*/_jsx(Text, {
                      as: "p",
                      children: /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "upgrades.communicationMethods.scheduleAMeeting.exploreUseCases"
                      })
                    })]
                  }), /*#__PURE__*/_jsxs(Flex, {
                    flexDirection: "row",
                    gap: "200",
                    alignItems: "center",
                    children: [/*#__PURE__*/_jsx(SuccessIcon, {
                      color: "fill-accent-green-default"
                    }), /*#__PURE__*/_jsx(Text, {
                      as: "p",
                      children: /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "upgrades.communicationMethods.scheduleAMeeting.learnMore"
                      })
                    })]
                  }), /*#__PURE__*/_jsxs(Flex, {
                    flexDirection: "row",
                    gap: "200",
                    alignItems: "center",
                    children: [/*#__PURE__*/_jsx(SuccessIcon, {
                      color: "fill-accent-green-default"
                    }), /*#__PURE__*/_jsx(Text, {
                      as: "p",
                      children: /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "upgrades.communicationMethods.scheduleAMeeting.discussPricing"
                      })
                    })]
                  })]
                })]
              })]
            })
          }), /*#__PURE__*/_jsx(SelectableButton, {
            "data-test-id": "communication-method-email",
            size: "large",
            block: true,
            blockSize: "165px",
            onClick: () => handleClick(MODAL_TYPES.EMAIL, 'clicked send email'),
            title: /*#__PURE__*/_jsxs(Flex, {
              flexDirection: "row",
              gap: "400",
              alignItems: "center",
              paddingInlineStart: "400",
              paddingBlockEnd: "400",
              children: [/*#__PURE__*/_jsx(Illustration, {
                name: "successfully-connected-email",
                height: 75
              }), /*#__PURE__*/_jsxs(Flex, {
                flexDirection: "column",
                gap: "200",
                alignItems: "start",
                children: [/*#__PURE__*/_jsx(Text, {
                  as: "h5",
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "upgrades.communicationMethods.email"
                  })
                }), /*#__PURE__*/_jsx(Text, {
                  as: "p",
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "upgrades.communicationMethods.sendAnEmail"
                  })
                })]
              })]
            })
          }), /*#__PURE__*/_jsx(SelectableButton, {
            "data-test-id": "communication-method-call",
            size: "large",
            block: true,
            blockSize: "165px",
            onClick: () => handleClick(MODAL_TYPES.GET_A_CALL, 'clicked contact sales with phone call'),
            title: /*#__PURE__*/_jsxs(Flex, {
              flexDirection: "row",
              gap: "400",
              alignItems: "center",
              paddingInlineStart: "400",
              paddingBlockEnd: "400",
              children: [/*#__PURE__*/_jsx(Illustration, {
                name: "calling-small",
                height: 75
              }), /*#__PURE__*/_jsxs(Flex, {
                flexDirection: "column",
                gap: "200",
                alignItems: "start",
                children: [/*#__PURE__*/_jsx(Text, {
                  as: "h5",
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "upgrades.communicationMethods.call"
                  })
                }), /*#__PURE__*/_jsx(Text, {
                  as: "p",
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "upgrades.communicationMethods.getACall"
                  })
                })]
              })]
            })
          }), isFreePortal && /*#__PURE__*/_jsx(SelectableButton, {
            "data-test-id": "communication-method-chat",
            size: "large",
            block: true,
            blockSize: "165px",
            selected: false,
            title: /*#__PURE__*/_jsxs(Flex, {
              flexDirection: "row",
              gap: "400",
              alignItems: "center",
              paddingInlineStart: "400",
              paddingBlockEnd: "400",
              children: [/*#__PURE__*/_jsx(Illustration, {
                name: "contacts-chat",
                height: 75,
                marginBlockStart: "700"
              }), /*#__PURE__*/_jsxs(Flex, {
                flexDirection: "column",
                gap: "200",
                alignItems: "start",
                children: [/*#__PURE__*/_jsx(Text, {
                  as: "h5",
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "upgrades.communicationMethods.chatWithUs"
                  })
                }), /*#__PURE__*/_jsx(Text, {
                  as: "p",
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "upgrades.communicationMethods.getQuickAnswers"
                  })
                })]
              })]
            }),
            onClick: handleClickChat
          })]
        })]
      })
    })]
  }));
};